/**
 * WebSocket-Verbindung zum {@code /ws/events}-Endpoint der Dashboard-API.
 *
 * Verantwortlichkeiten:
 *  - Verbindung aufbauen, sobald der User angemeldet ist
 *  - Bei Disconnect: Reconnect mit exponential backoff
 *  - Eingehende Events deserialisieren und an den passenden Pinia-Store
 *    routen
 *  - Bei Logout: sauber schließen, kein Reconnect
 *
 * Token-Authentifizierung: WebSocket-Standard erlaubt keine Custom-
 * Header beim Handshake. Wir hängen das Token als Query-Param an —
 * der JwtAuthFilter im Backend kann's auch aus dort lesen, sobald
 * das in Phase 11 erweitert wird. Aktuell kommt der WS-Handshake
 * über die ohnehin-authentisierte Session des HTTP-Handshakes durch.
 */
import { useAuthStore } from '@/stores/auth';
import { useServicesStore } from '@/stores/services';
import type { EventDto } from '@/types/api';
import { ref, type Ref } from 'vue';

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

export class EventStream {
  private socket: WebSocket | null = null;
  private backoffMs = INITIAL_BACKOFF_MS;
  private reconnectTimer: number | null = null;
  private shuttingDown = false;
  private listeners: Array<(e: EventDto) => void> = [];

  /** Schritt 7: reaktiver Connection-State fuer UI-Indicator. */
  public readonly connected: Ref<boolean> = ref(false);

  start() {
    this.shuttingDown = false;
    this.connect();
  }

  stop() {
    this.shuttingDown = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  /**
   * Externer Subscriber-Hook. Komponenten können sich für *alle* Events
   * registrieren (z.B. um Toasts bei MOTD-Updates anzuzeigen). Die
   * gestores-internen Subscribers laufen unabhängig davon.
   */
  on(callback: (e: EventDto) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((c) => c !== callback);
    };
  }

  private connect() {
    const auth = useAuthStore();
    if (!auth.token) {
      // Ohne Token kein WS-Handshake — wir warten passiv, bis Login
      // den Stream re-startet
      return;
    }

    const url = this.buildUrl(auth.token);
    try {
      this.socket = new WebSocket(url);
    } catch (e) {
      console.warn('WebSocket-Erstellung fehlgeschlagen:', e);
      this.scheduleReconnect();
      return;
    }

    this.socket.onopen = () => {
      console.info('WebSocket verbunden');
      this.backoffMs = INITIAL_BACKOFF_MS;
      this.connected.value = true;
    };

    this.socket.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data) as EventDto;
        this.dispatch(event);
      } catch (e) {
        console.warn('Ungültiges Event-JSON:', e);
      }
    };

    this.socket.onerror = (e) => {
      console.warn('WebSocket-Fehler:', e);
    };

    this.socket.onclose = (event) => {
      this.socket = null;
      this.connected.value = false;
      // Phase 18: Server schickt Close-Code 4401, wenn das Token abgelaufen
      // oder revoked ist (siehe WebSocketTokenWatchdog). In dem Fall NICHT
      // reconnect-loopen — der Token ist invalide, der nächste Handshake
      // würde sowieso mit 401 scheitern. Stattdessen Auth-Store flushen
      // und auf die Login-Seite redirecten.
      if (event.code === 4401) {
        console.info('WebSocket: Auth required — Logout-Redirect');
        const auth = useAuthStore();
        this.shuttingDown = true; // Reconnect verhindern
        // Logout async, dann harter Redirect — Vue-Router-Import würde
        // hier zirkulär werden (router → views → stores → eventStream)
        auth.logout().finally(() => {
          if (location.pathname !== '/login') {
            location.href = '/login';
          }
        });
        return;
      }
      if (!this.shuttingDown) this.scheduleReconnect();
    };
  }

  private buildUrl(token: string): string {
      const apiBase = import.meta.env.VITE_API_BASE_URL;
      if (apiBase) {
        // Production: WS-Host aus konfigurierter API-Base-URL ableiten
        const url = new URL(apiBase);
        const protocol = url.protocol === 'https:' ? 'wss' : 'ws';
        return `${protocol}://${url.host}/ws/events?token=${encodeURIComponent(token)}`;
      }
      // Dev: relativ zum Page-Origin (Vite-Proxy übernimmt)
      const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
      return `${protocol}://${location.host}/ws/events?token=${encodeURIComponent(token)}`;
    }

  private scheduleReconnect() {
    if (this.shuttingDown) return;
    const delay = this.backoffMs;
    console.info(`WebSocket-Reconnect in ${delay}ms`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
      this.connect();
    }, delay);
  }

  private dispatch(event: EventDto) {
    // 1) Stores-internes Routing
    switch (event.type) {
      case 'service-update': {
        const services = useServicesStore();
        services.applyServiceUpdate(
          event.payload as {
            name: string;
            group: string;
            state: string;
            online: number;
            max: number;
          }
        );
        break;
      }
      case 'service-announced': {
        const services = useServicesStore();
        services.applyServiceAnnounced(
          event.payload as { name: string; group: string; host: string; port: number }
        );
        break;
      }
      // Phase 26: sauberer Service-Stop. Anders als bei CRASHED nehmen
      // wir den Service aus der Liste raus statt ihn mit DEAD-State zu
      // belassen — Operator hat ja explizit Stop angefragt.
      case 'service-removed': {
        const services = useServicesStore();
        const p = event.payload as { name: string };
        if (p.name) services.removeService(p.name);
        break;
      }
      // motd-changed / maintenance-started / maintenance-ended haben
      // breite Implikationen (Liste neu laden); lassen wir Komponenten
      // via Listener-Hook selbst handhaben
      default:
        break;
    }
    // 2) Externe Subscriber
    for (const cb of this.listeners) cb(event);
  }
}

/** Singleton — eine WS-Verbindung pro Browser-Tab. */
export const eventStream = new EventStream();
